// videoResourceService.js - 处理视频资源映射和时间管理
// 这个服务模块用于将后端的视频ID映射到前端可访问的URL，并处理视频片段的时间管理

/**
 * 视频ID到URL的映射规则
 * 基于以下格式:
 * - N7_1080p -> /night/videos/video7.mp4
 * - D5_1080p -> /day/videos/video5.mp4
 * 其中 N 代表夜间, D 代表白天
 */
 
// 缓存已经映射过的视频ID，提高性能
const videoUrlCache = new Map();

/**
 * 根据后端视频ID获取前端视频URL
 * 映射规则：
 * - N7_1080p -> /night/videos/video7.mp4
 * - D5_1080p -> /day/videos/video5.mp4
 * 
 * @param {string} videoId - 后端提供的视频ID
 * @returns {string|null} 前端视频路径
 */
export const getVideoUrl = (videoId) => {
  if (!videoId) return null;
  
  // 检查缓存
  if (videoUrlCache.has(videoId)) {
    return videoUrlCache.get(videoId);
  }
  
  const typeMatch = videoId.match(/^([ND])(\d+)_/i);
  
  if (!typeMatch) {
    console.warn(`非标准视频ID格式: ${videoId}，尝试使用通用映射`);
    try {
      // 后备方案：也返回前端路径，不再用5000端口
      const fallbackUrl = `/videos/${videoId}.mp4`;
      videoUrlCache.set(videoId, fallbackUrl);
      return fallbackUrl;
    } catch (error) {
      console.error('处理视频ID时出错:', error);
      return null;
    }
  }
  
  const [_, type, number] = typeMatch;
  const timePeriod = type.toUpperCase() === 'N' ? 'night' : 'day';
  
  const url = `/${timePeriod}/videos/video${number}.mp4`;
  videoUrlCache.set(videoId, url);
  return url;
};


/**
 * 根据视频ID和时间戳获取视频片段信息
 * @param {string} videoId - 视频的唯一标识符
 * @param {string} timestamp - 特定时间点，格式为 "H:MM:SS" 或 "HH:MM:SS"
 * @param {Object} videoSegmentData - 可选的视频片段数据，直接来自后端
 * @returns {Object} 包含startTime和endTime的对象（秒为单位）
 */
export const getVideoSegment = (videoId, timestamp, videoSegmentData = null) => {
  if (!videoId || !timestamp) {
    return { startTime: 0, endTime: null };
  }
  
  // 如果提供了视频片段数据，优先使用它
  if (videoSegmentData && videoSegmentData.startTime && videoSegmentData.endTime) {
    return {
      startTime: parseTimeToSeconds(videoSegmentData.startTime),
      endTime: parseTimeToSeconds(videoSegmentData.endTime)
    };
  }
  
  // 否则，创建一个围绕时间戳的片段（前后5秒）
  const timestampSeconds = parseTimeToSeconds(timestamp);
  return {
    startTime: Math.max(0, timestampSeconds - 5),  // 确保不小于0
    endTime: timestampSeconds + 5
  };
};

/**
 * 将时间字符串转换为秒数
 * 支持多种格式: "HH:MM:SS", "MM:SS", "M:SS", "SS", "H:MM:SS"
 * 
 * @param {string} timeStr - 时间字符串
 * @returns {number} 总秒数
 */
export const parseTimeToSeconds = (timeStr) => {
  if (!timeStr) return 0;
  
  // 处理可能是数字的情况
  if (!isNaN(timeStr)) {
    return Number(timeStr);
  }
  
  // 处理不同格式的时间字符串
  try {
    const parts = timeStr.split(':').map(part => parseInt(part.trim(), 10));
    
    if (parts.length === 3) {
      // HH:MM:SS 格式
      return parts[0] * 3600 + parts[1] * 60 + parts[2];
    } else if (parts.length === 2) {
      // MM:SS 格式
      return parts[0] * 60 + parts[1];
    } else if (parts.length === 1 && !isNaN(parts[0])) {
      // 单纯秒数
      return parts[0];
    }
  } catch (error) {
    console.error(`无法解析时间字符串: "${timeStr}"`, error);
  }
  
  // 无法解析的格式，返回0
  console.warn(`无法识别的时间格式: "${timeStr}", 返回默认值0`);
  return 0;
};

/**
 * 将秒数转换为格式化时间字符串
 * @param {number} seconds - 秒数
 * @returns {string} 格式化的时间字符串 "MM:SS" 或 "HH:MM:SS"
 */
export const formatTimeFromSeconds = (seconds) => {
  if (seconds === null || seconds === undefined) return 'N/A';
  
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  
  // 如果小于1小时，使用 MM:SS 格式，否则使用 HH:MM:SS
  return h > 0 
    ? `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
    : `${m}:${s.toString().padStart(2, '0')}`;
};

/**
 * 获取视频元数据
 * 根据提供的原始数据对象提取和计算完整的视频元数据
 * 
 * @param {Object} rawData - 原始对象数据
 * @returns {Object|null} 包含视频元数据的对象或null（如果无有效数据）
 */
export const getVideoMetadata = (rawData) => {
  if (!rawData) return null;
  
  // 解析原始数据以找到视频信息
  let appearance = null;
  let videoSegmentData = null;
  
  // 处理不同的数据结构
  if (rawData.appearances && rawData.appearances.length > 0) {
    appearance = rawData.appearances[0];
    videoSegmentData = appearance.videoSegment;
  } else if (rawData.rawPersonData && rawData.rawPersonData.appearances && rawData.rawPersonData.appearances.length > 0) {
    appearance = rawData.rawPersonData.appearances[0];
    videoSegmentData = appearance.videoSegment;
  } else if (rawData.videoID || rawData.videoId) {
    appearance = {
      videoId: rawData.videoID || rawData.videoId,
      timestamp: rawData.timestamp
    };
    videoSegmentData = rawData.videoSegment;
  }
  
  if (!appearance) {
    console.warn('没有找到有效的视频信息', rawData);
    return null;
  }
  
  // 确保我们有一个统一的videoId字段
  const videoId = appearance.videoID || appearance.videoId;
  
  if (!videoId) {
    console.warn('无效的视频ID', appearance);
    return null;
  }
  
  // 获取视频URL
  const videoUrl = getVideoUrl(videoId);
  
  if (!videoUrl) {
    console.warn(`无法获取视频URL，视频ID: ${videoId}`);
    return null;
  }
  
  // 获取视频片段时间
  const segment = getVideoSegment(videoId, appearance.timestamp, videoSegmentData);
  
  // 计算片段持续时间
  const duration = segment.endTime !== null ? segment.endTime - segment.startTime : 10;
  
  return {
    videoUrl,
    startTime: segment.startTime,
    endTime: segment.endTime,
    duration,
    displayName: `${videoId} (${formatTimeFromSeconds(segment.startTime)} - ${segment.endTime !== null ? formatTimeFromSeconds(segment.endTime) : 'End'})`,
    timestamp: appearance.timestamp,
    videoId: videoId,
    imageUrl: appearance.imagePath
  };
};

/**
 * 清除视频URL缓存
 * 在视频资源发生变化或需要重新加载时使用
 */
export const clearVideoUrlCache = () => {
  videoUrlCache.clear();
};